import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { BarShapeProps, TooltipContentProps } from "recharts";
import { useTranslation } from "react-i18next";
import { cn, Icon, Skeleton } from "@nexus/ui";
import { usePrefersReducedMotion } from "@/features/overview/components/OverviewChart/usePrefersReducedMotion";
import { formatEventWindowShort } from "@/features/events/constants/event-time-ranges";
import { formatPreciseDurationMs } from "@/features/events/lib/event-timing";
import type { ScheduledJob } from "@/features/scheduled-jobs/api/scheduled-jobs.api";
import {
  humanizeScheduledJobId,
  scheduledJobIdTooltip,
} from "../lib/scheduled-job-display";
import {
  buildScheduledJobRunSeries,
  type ScheduledJobRunPoint,
  type ScheduledJobRunSeries,
} from "../lib/scheduled-job-run-series";

interface ScheduledJobsActivityChartProps {
  jobs: ScheduledJob[];
  isLoading: boolean;
  /** True while TanStack still shows the previous window. */
  isPlaceholderData: boolean;
  /** Lookback that scoped the fetch — used in the accessible summary. */
  windowHours: number;
  /** Job resource name currently open in the drawer — dims the other stems. */
  selectedJobName: string | null;
  onSelectJob: (jobName: string) => void;
  /** Peak-failure callout → health=failing filter (Events Failed pattern). */
  onFilterFailed: () => void;
  isFailedFilterActive: boolean;
}

/**
 * Compact companion to the jobs table. Sparse cron history reads as a
 * lollipop timeline (stem + head), not fat category bars — two daily runs
 * should not paint as two slabs across the full band.
 */
const CHART_HEIGHT_PX = 132;
const DURATION_AXIS_WIDTH = 48;
const AXIS_MIN_TICK_GAP = 36;
const UNSELECTED_OPACITY = 0.28;
const HOVER_PREVIEW_OPACITY = 0.6;
const TOOLTIP_TRACK_MS = 150;
const ENTRANCE_MS = 380;
const ENTRANCE_STAGGER_MS = 55;
const ENTRANCE_BEGIN_MS = 40;
const LOLLIPOP_HEAD_R = 5;
const LOLLIPOP_STEM_W = 2;
const HIT_TARGET_HALF_W = 14;

/** Matches EventsActivityChart / RoleDrawer collapse pattern. */
const COLLAPSE_CLASS =
  "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none";

const SERIES_COLORS = {
  completed: "var(--chart-spend)",
  failed: "var(--error)",
  inFlight: "var(--info)",
  avg: "var(--muted-foreground)",
  p95: "var(--chart-orange)",
} as const;

const AXIS_TICK = { fontSize: 10, fill: "var(--muted-foreground)" } as const;
const LABEL_CLASS = "text-[11px] uppercase tracking-wider text-muted-foreground";

function resolveStemOpacity(
  point: ScheduledJobRunPoint,
  selectedJobName: string | null,
  hoveredRunId: string | null,
): number {
  const isHovered = point.id === hoveredRunId;

  if (selectedJobName != null) {
    if (point.jobName === selectedJobName) return 1;
    return isHovered ? HOVER_PREVIEW_OPACITY : UNSELECTED_OPACITY;
  }

  if (hoveredRunId == null) return 1;
  return isHovered ? 1 : HOVER_PREVIEW_OPACITY;
}

function LegendDot({
  color,
  label,
  hollow = false,
  variant = "dot",
}: {
  color: string;
  label: string;
  hollow?: boolean;
  variant?: "dot" | "dash";
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {variant === "dash" ? (
        <span
          aria-hidden
          className="h-0 w-2.5 shrink-0 border-t border-dashed"
          style={{ borderColor: color }}
        />
      ) : (
        <span
          aria-hidden
          className={cn("size-1.5 shrink-0 rounded-full", hollow && "border border-current bg-transparent")}
          style={hollow ? { color, borderColor: color } : { backgroundColor: color }}
        />
      )}
      <span className={LABEL_CLASS}>{label}</span>
    </span>
  );
}

/**
 * Recharts category bands stretch to fill the chart — with two daily runs that
 * turns into two fat slabs. Draw a stem + head centered in the band instead,
 * and keep a wider invisible hit target so the pointer still feels generous.
 *
 * Recharts' own bar grow is unreliable with a custom `shape`, so on-mount
 * motion is a CSS scaleY from the baseline (see `.sj-lollipop-enter`).
 */
function RunLollipop({
  entranceActive = false,
  ...props
}: BarShapeProps & { entranceActive?: boolean }) {
  const {
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    fill = SERIES_COLORS.completed,
    fillOpacity = 1,
    payload,
    index = 0,
  } = props;

  const point = payload as ScheduledJobRunPoint | undefined;
  const cx = x + width / 2;
  const baseline = y + height;
  const isHollow = point != null && (!point.triggeredBySchedule || point.isDurationUnknown);
  const opacity = typeof fillOpacity === "number" ? fillOpacity : 1;
  const stemIndex = typeof index === "number" ? index : 0;

  if (height <= 0) return null;

  return (
    <g
      className={cn(
        "motion-safe:transition-opacity motion-safe:duration-150 motion-safe:ease-out",
        entranceActive && "sj-lollipop-enter",
      )}
      style={
        entranceActive
          ? {
              ["--sj-lollipop-delay" as string]: `${ENTRANCE_BEGIN_MS + stemIndex * ENTRANCE_STAGGER_MS}ms`,
            }
          : undefined
      }
    >
      <rect
        x={cx - HIT_TARGET_HALF_W}
        y={y}
        width={HIT_TARGET_HALF_W * 2}
        height={Math.max(height, LOLLIPOP_HEAD_R * 2)}
        fill="transparent"
      />
      <line
        x1={cx}
        y1={baseline}
        x2={cx}
        y2={y + LOLLIPOP_HEAD_R}
        stroke={fill}
        strokeOpacity={opacity}
        strokeWidth={LOLLIPOP_STEM_W}
        strokeLinecap="round"
      />
      <circle
        cx={cx}
        cy={y}
        r={LOLLIPOP_HEAD_R}
        fill={isHollow ? "var(--card)" : fill}
        fillOpacity={isHollow ? 1 : opacity}
        stroke={fill}
        strokeOpacity={opacity}
        strokeWidth={2}
      />
    </g>
  );
}

function RunTooltip({
  active,
  payload,
  series,
}: TooltipContentProps<number, string> & { series: ScheduledJobRunSeries }) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as ScheduledJobRunPoint | undefined;
  if (!point) return null;

  const outcomeLabel =
    point.outcome === "completed"
      ? t("admin.scheduledJobs.activity.completed", "Completed")
      : point.outcome === "failed"
        ? t("admin.scheduledJobs.activity.failed", "Failed")
        : t("admin.scheduledJobs.activity.inFlight", "In flight");

  const durationLabel = point.isDurationUnknown
    ? t("admin.scheduledJobs.activity.durationUnknown", "Duration unknown")
    : formatPreciseDurationMs(point.durationMs);

  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-md">
      <p className="mb-1.5 font-medium text-foreground">{point.rangeLabel}</p>
      <ul className="space-y-0.5">
        <li className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="text-muted-foreground">
            {t("admin.scheduledJobs.activity.job", "Job")}
          </span>
          <span
            className="ml-auto max-w-[12rem] truncate pl-3 font-medium text-foreground"
            title={scheduledJobIdTooltip(point.jobId)}
          >
            {humanizeScheduledJobId(point.jobId)}
          </span>
        </li>
        <li className="flex items-center gap-1.5 whitespace-nowrap">
          <span
            aria-hidden
            className="size-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: SERIES_COLORS[point.outcome] }}
          />
          <span className="text-muted-foreground">{outcomeLabel}</span>
          <span className="ml-auto pl-3 font-medium tabular-nums text-foreground">
            {durationLabel}
          </span>
        </li>
        <li className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="text-muted-foreground">
            {point.triggeredBySchedule
              ? t("admin.scheduledJobs.drawer.bySchedule", "scheduler")
              : t("admin.scheduledJobs.drawer.byOperator", "manual")}
          </span>
        </li>
        {point.errorMessage ? (
          <li className="max-w-[240px] truncate text-error" title={point.errorMessage}>
            {point.errorMessage}
          </li>
        ) : null}
      </ul>

      <div className="mt-2 border-t border-border pt-1.5">
        <ul className="space-y-0.5">
          <li className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
            <span>{t("admin.scheduledJobs.activity.tooltipWindow", "In chart")}</span>
            <span className="ml-auto pl-3 tabular-nums text-foreground">
              {t("admin.scheduledJobs.activity.tooltipTotals", "{{total}} · {{failed}} failed", {
                total: series.total.toLocaleString(i18n.language),
                failed: series.failed.toLocaleString(i18n.language),
              })}
            </span>
          </li>
          {series.avgDurationMs != null ? (
            <li className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
              <span>{t("admin.scheduledJobs.activity.avgLabel", "Avg")}</span>
              <span className="ml-auto pl-3 tabular-nums text-foreground">
                {formatPreciseDurationMs(series.avgDurationMs)}
              </span>
            </li>
          ) : null}
          {series.p95DurationMs != null ? (
            <li className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
              <span>{t("admin.scheduledJobs.activity.p95Label", "p95")}</span>
              <span className="ml-auto pl-3 tabular-nums text-foreground">
                {formatPreciseDurationMs(series.p95DurationMs)}
              </span>
            </li>
          ) : null}
          {series.jobsTouched > 0 ? (
            <li className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
              <span>{t("admin.scheduledJobs.activity.jobsTouched", "Jobs")}</span>
              <span className="ml-auto pl-3 tabular-nums text-foreground">
                {series.jobsTouched.toLocaleString(i18n.language)}
              </span>
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}

/**
 * Recent-run duration timeline across every Cloud Scheduler job on the page.
 * Clicking a stem opens that job's drawer — same action as selecting its row.
 *
 * On-mount: stems scale up from the baseline with a left-to-right stagger.
 * Re-armed when a new window's own payload lands (Events/Token pattern).
 */
export function ScheduledJobsActivityChart({
  jobs,
  isLoading,
  isPlaceholderData,
  windowHours,
  selectedJobName,
  onSelectJob,
  onFilterFailed,
  isFailedFilterActive,
}: ScheduledJobsActivityChartProps) {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const prefersReducedMotion = usePrefersReducedMotion();

  const series = useMemo(
    () => buildScheduledJobRunSeries(jobs, i18n.language),
    [jobs, i18n.language],
  );

  const dataMatchesRequest = !isPlaceholderData && !isLoading;
  const [hasGrown, setHasGrown] = useState(false);
  const [armedWindow, setArmedWindow] = useState<number | null>(null);
  const [entranceEpoch, setEntranceEpoch] = useState(0);
  const [hoveredRunId, setHoveredRunId] = useState<string | null>(null);

  if (dataMatchesRequest && armedWindow !== windowHours) {
    setArmedWindow(windowHours);
    setHasGrown(false);
    setEntranceEpoch(entranceEpoch + 1);
    setHoveredRunId(null);
  }

  const isEntranceActive = !prefersReducedMotion && !hasGrown && dataMatchesRequest;
  const entranceActiveRef = useRef(isEntranceActive);
  entranceActiveRef.current = isEntranceActive;

  useEffect(() => {
    if (!isEntranceActive || series.points.length === 0) return;
    const lastDelay = ENTRANCE_STAGGER_MS * Math.max(0, series.points.length - 1);
    const id = window.setTimeout(
      () => setHasGrown(true),
      ENTRANCE_BEGIN_MS + lastDelay + ENTRANCE_MS + 40,
    );
    return () => window.clearTimeout(id);
  }, [isEntranceActive, series.points.length, entranceEpoch]);

  // Stable identity — a new `shape` fn every render restarts CSS grow mid-flight.
  const renderLollipop = useCallback(
    (props: BarShapeProps) => (
      <RunLollipop {...props} entranceActive={entranceActiveRef.current} />
    ),
    [],
  );

  const pointAt = (
    activeIndex: number | string | null | undefined,
  ): ScheduledJobRunPoint | undefined => {
    if (activeIndex == null) return undefined;
    const index = Number(activeIndex);
    return Number.isInteger(index) ? series.points[index] : undefined;
  };

  const summary = t(
    "admin.scheduledJobs.activity.summary",
    "Run history over the last {{window}}: {{total}} runs, {{failed}} failed across {{jobs}} jobs.",
    {
      window: formatEventWindowShort(windowHours, t),
      total: series.total.toLocaleString(i18n.language),
      failed: series.failed.toLocaleString(i18n.language),
      jobs: series.jobsTouched.toLocaleString(i18n.language),
    },
  );

  const canFilterFailed = series.failed > 0 || isFailedFilterActive;

  return (
    <section className="relative z-10 shrink-0 rounded-md border border-border bg-card">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setIsExpanded((open) => !open)}
          aria-expanded={isExpanded}
          aria-controls="admin-scheduled-jobs-activity-chart"
          className="inline-flex cursor-pointer items-center gap-1 rounded-sm text-left transition-colors duration-150 ease-out hover:text-foreground motion-reduce:transition-none"
        >
          <Icon
            name="chevron-down"
            size="xs"
            aria-hidden
            className={cn(
              "text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none",
              !isExpanded && "-rotate-90",
            )}
          />
          <span className={LABEL_CLASS}>
            {t("admin.scheduledJobs.activity.title", "Run history")}
          </span>
        </button>

        {series.total > 0 ? (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {t("admin.scheduledJobs.activity.runCount", "{{count}} runs", {
              count: series.total,
            })}
            {series.jobsTouched > 0 ? (
              <>
                <span className="text-border"> · </span>
                {t("admin.scheduledJobs.activity.jobsCount", "{{count}} jobs", {
                  count: series.jobsTouched,
                })}
              </>
            ) : null}
            {series.manualCount > 0 ? (
              <>
                <span className="text-border"> · </span>
                {t("admin.scheduledJobs.activity.manualCount", "{{count}} manual", {
                  count: series.manualCount,
                })}
              </>
            ) : null}
          </span>
        ) : null}

        {series.slowest && series.slowest.durationMs > 0 ? (
          <button
            type="button"
            onClick={() => onSelectJob(series.slowest!.jobName)}
            className={cn(
              "inline-flex cursor-pointer items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-[11px] transition-colors duration-150 ease-out hover:bg-foreground/5 motion-reduce:transition-none",
              "motion-safe:animate-in fade-in slide-in-from-left-1 animation-duration-200",
              selectedJobName === series.slowest.jobName && "bg-muted hover:bg-muted",
            )}
            title={scheduledJobIdTooltip(series.slowest.jobId)}
          >
            <Icon name="clock" size="xs" aria-hidden />
            <span className="text-foreground">
              {t("admin.scheduledJobs.activity.slowest", "Slowest {{duration}} · {{job}}", {
                duration: formatPreciseDurationMs(series.slowest.durationMs),
                job: humanizeScheduledJobId(series.slowest.jobId),
              })}
            </span>
          </button>
        ) : null}

        {canFilterFailed ? (
          <button
            type="button"
            onClick={onFilterFailed}
            disabled={series.failed === 0 && !isFailedFilterActive}
            aria-pressed={isFailedFilterActive}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-[11px] transition-colors duration-150 ease-out hover:bg-foreground/5 motion-reduce:transition-none",
              "motion-safe:animate-in fade-in slide-in-from-left-1 animation-duration-200",
              isFailedFilterActive && "bg-error-subtle hover:bg-error-subtle",
              series.failed === 0 && !isFailedFilterActive && "cursor-default opacity-60",
            )}
          >
            <Icon name="alert-circle" size="xs" colorVar="--error" aria-hidden />
            <span className="text-foreground">
              {t("admin.scheduledJobs.activity.failedCount", "{{count}} failed", {
                count: series.failed,
              })}
            </span>
          </button>
        ) : null}

        <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
          <LegendDot
            color={SERIES_COLORS.completed}
            label={t("admin.scheduledJobs.activity.completed", "Completed")}
          />
          <LegendDot
            color={SERIES_COLORS.failed}
            label={t("admin.scheduledJobs.activity.failed", "Failed")}
          />
          <LegendDot
            color={SERIES_COLORS.inFlight}
            label={t("admin.scheduledJobs.activity.inFlight", "In flight")}
            hollow
          />
          {series.avgDurationMs != null ? (
            <LegendDot
              color={SERIES_COLORS.avg}
              label={t("admin.scheduledJobs.activity.avgLegend", "Avg")}
              variant="dash"
            />
          ) : null}
          {series.p95DurationMs != null ? (
            <LegendDot
              color={SERIES_COLORS.p95}
              label={t("admin.scheduledJobs.activity.p95Legend", "p95")}
              variant="dash"
            />
          ) : null}
        </div>
      </div>

      <div
        id="admin-scheduled-jobs-activity-chart"
        inert={!isExpanded || undefined}
        className={cn(COLLAPSE_CLASS, isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              "px-1 pt-1 pb-2 transition-opacity duration-200",
              isPlaceholderData && "opacity-40",
            )}
          >
            {isLoading && series.points.length === 0 ? (
              <Skeleton className="w-full rounded-sm" style={{ height: CHART_HEIGHT_PX }} />
            ) : series.total === 0 ? (
              <div
                className="flex items-center justify-center text-xs text-muted-foreground"
                style={{ height: CHART_HEIGHT_PX }}
              >
                {t("admin.scheduledJobs.activity.empty", "No recent runs to chart yet")}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
                <ComposedChart
                  data={series.points}
                  margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
                  title={summary}
                  barCategoryGap="28%"
                  onClick={(state) => {
                    const point = pointAt(state?.activeTooltipIndex);
                    if (!point) return;
                    onSelectJob(point.jobName);
                  }}
                  onMouseMove={(state) => {
                    if (isEntranceActive) return;
                    const next = pointAt(state?.activeTooltipIndex)?.id ?? null;
                    setHoveredRunId((current) => (current === next ? current : next));
                  }}
                  onMouseLeave={() => setHoveredRunId(null)}
                  className="cursor-pointer"
                >
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={AXIS_TICK}
                    tickLine={false}
                    axisLine={{ stroke: "var(--border)" }}
                    interval="preserveStartEnd"
                    minTickGap={AXIS_MIN_TICK_GAP}
                  />
                  <YAxis
                    tick={AXIS_TICK}
                    tickLine={false}
                    axisLine={false}
                    width={DURATION_AXIS_WIDTH}
                    tickFormatter={(value: number) => formatPreciseDurationMs(value)}
                    domain={series.hasDuration ? [0, "auto"] : [0, 1]}
                  />
                  {series.avgDurationMs != null ? (
                    <ReferenceLine
                      y={series.avgDurationMs}
                      stroke={SERIES_COLORS.avg}
                      strokeDasharray="4 4"
                      strokeOpacity={isEntranceActive ? 0 : 0.45}
                      ifOverflow="extendDomain"
                      className="motion-safe:transition-[stroke-opacity] motion-safe:duration-200 motion-safe:ease-out"
                    />
                  ) : null}
                  {series.p95DurationMs != null ? (
                    <ReferenceLine
                      y={series.p95DurationMs}
                      stroke={SERIES_COLORS.p95}
                      strokeDasharray="4 4"
                      strokeOpacity={isEntranceActive ? 0 : 0.7}
                      ifOverflow="extendDomain"
                      className="motion-safe:transition-[stroke-opacity] motion-safe:duration-200 motion-safe:ease-out"
                    />
                  ) : null}
                  <Tooltip
                    content={(props) => (
                      <RunTooltip
                        {...(props as TooltipContentProps<number, string>)}
                        series={series}
                      />
                    )}
                    cursor={{ stroke: "var(--border)", strokeDasharray: "3 3" }}
                    animationDuration={TOOLTIP_TRACK_MS}
                    animationEasing="ease-out"
                  />
                  <Bar
                    dataKey="plotDurationMs"
                    shape={renderLollipop}
                    isAnimationActive={false}
                  >
                    {series.points.map((point) => (
                      <Cell
                        key={point.id}
                        fill={SERIES_COLORS[point.outcome]}
                        fillOpacity={resolveStemOpacity(point, selectedJobName, hoveredRunId)}
                      />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
